import { useCallback } from "react";
import { useRouter } from "next/router";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";

type RangeBound = number | undefined;

export const LISTING_KEY_TO_QUERY_PARAM = {
  brands: "brand_slug",
  category: "cat_slug",
  colors: "colors",
  discount: "discount",
  genders: "genders",
  pattern: "patterns",
  price: "price",
  size: "sizes",
  style: "styles",
  collections: "collections",
} as const satisfies Record<ListingFacetKeys, string>;

export const QUERY_PARAM_TO_LISTING_KEY = Object.fromEntries(
  Object.entries({ ...LISTING_KEY_TO_QUERY_PARAM }).map(([k, v]) => [v, k])
) as {
  [K in keyof typeof LISTING_KEY_TO_QUERY_PARAM as (typeof LISTING_KEY_TO_QUERY_PARAM)[K]]: K;
};

function resolveQueryParam(facet: string): string {
  return LISTING_KEY_TO_QUERY_PARAM[facet as ListingFacetKeys] ?? facet;
}

export function useHandleSearchParams(): {
  handleSearchParams: (props: {
    facet: string;
    value: string;
    values?: string[];
    isSingle?: boolean;
    shouldDelete?: boolean;
    deletePage?: boolean;
  }) => void;
  handleSearchParamsPrice: ({
    min,
    max,
  }: {
    min?: RangeBound;
    max?: RangeBound;
  }) => void;
} {
  const router = useRouter();

  const handleSearchParams = useCallback(
    ({
      facet,
      value,
      values,
      isSingle,
      shouldDelete = false,
      deletePage = false,
    }: {
      facet: string;
      value: string;
      values?: string[];
      isSingle?: boolean;
      shouldDelete?: boolean;
      deletePage?: boolean;
    }) => {
      const url = new URL(location.toString());
      const paramKey = resolveQueryParam(facet);

      const selected = url.searchParams.has(paramKey, value);

      if (shouldDelete) {
        url.searchParams.delete(paramKey, value);

        void router.push(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      if (selected) {
        if (values) {
          for (const value of values) {
            url.searchParams.delete(paramKey, value);
          }
        } else {
          url.searchParams.delete(paramKey, value);
        }
      }

      if (!selected && !isSingle) {
        if (values) {
          for (const value of values) {
            url.searchParams.append(paramKey, value);
          }
        } else {
          url.searchParams.append(paramKey, value);
        }
      }

      if (!selected && isSingle && !Array.isArray(value)) {
        url.searchParams.set(paramKey, value);
      }

      if (deletePage && url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  const handleSearchParamsPrice = useCallback(
    ({ min, max }: { min?: RangeBound; max?: RangeBound }) => {
      const url = new URL(location.toString());

      if (min) {
        url.searchParams.set("min_price", `${min}`);
      }

      if (max) {
        url.searchParams.set("max_price", `${max}`);
      }

      if (url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  return { handleSearchParams, handleSearchParamsPrice };
}

export function useRemoveFilters(): {
  removeAllFiltersByKey: (
    facet: string,
    value?: string,
    deletePage?: boolean
  ) => void;
  removeAllFilters: (removeCategories?: boolean) => void;
  removePriceFilters: () => void;
} {
  const router = useRouter();

  const removeAllFilters = useCallback(
    (removeCategories?: boolean) => {
      const url = new URL(location.toString());

      for (const key of Object.keys(
        LISTING_KEY_TO_QUERY_PARAM
      ) as ListingFacetKeys[]) {
        if (key === "price" || key === "category") {
          continue;
        }
        url.searchParams.delete(LISTING_KEY_TO_QUERY_PARAM[key]);
      }

      url.searchParams.delete("min_price");
      url.searchParams.delete("max_price");
      url.searchParams.delete("page");

      if (removeCategories) {
        url.searchParams.delete(LISTING_KEY_TO_QUERY_PARAM.category);
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  const removePriceFilters = useCallback(() => {
    const url = new URL(location.toString());

    url.searchParams.delete("min_price");
    url.searchParams.delete("max_price");
    url.searchParams.delete("page");

    void router.push(url.toString(), undefined, {
      scroll: false,
      shallow: true,
    });
  }, [router]);

  const removeAllFiltersByKey = useCallback(
    (facet: string, value?: string, deletePage = true) => {
      const url = new URL(location.toString());
      const paramKey = resolveQueryParam(facet);

      if (!url.searchParams.has(paramKey)) {
        return;
      }

      if (value) {
        url.searchParams.delete(paramKey, value);
      } else {
        url.searchParams.delete(paramKey);
      }

      if (deletePage && url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  return { removeAllFilters, removePriceFilters, removeAllFiltersByKey };
}
